import { useEffect, useRef, useState } from "react";
import { LuPlus, LuX } from "react-icons/lu";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { InputGroup, InputGroupInput } from "@/components/ui/input-group";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  NumberField,
  NumberFieldDecrement,
  NumberFieldGroup,
  NumberFieldIncrement,
  NumberFieldInput,
} from "@/components/ui/number-field";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { TagInput, TagInputField, TagInputGroup } from "@/components/ui/tag-input";
import { useFieldSuggestions, usePatchScript } from "../hooks/use-review";
import { pickDirty } from "../lib/review-helpers";
import { useAutosave, type SaveStatus } from "../lib/use-autosave";
import type { ReviewScriptDetail, ScriptPatchInput } from "../lib/review-client";
import { SaveStatusLabel } from "./review-page-meta";
import { SuggestCombobox } from "./suggest-combobox";

type FormState = {
  label: string;
  rollNumber: string;
  studentName: string;
  gender: string;
  subject: string;
  section: string;
  groundTruthMarksTotal: number | null;
  tags: string[];
  notes: string;
  customFieldsEntries: [string, string][];
};

function toFormState(script: ReviewScriptDetail): FormState {
  return {
    label: script.label ?? "",
    rollNumber: script.rollNumber ?? "",
    studentName: script.studentName ?? "",
    gender: script.gender ?? "",
    subject: script.subject ?? "",
    section: script.section ?? "",
    groundTruthMarksTotal: script.groundTruthMarksTotal,
    tags: script.tags,
    notes: script.notes ?? "",
    customFieldsEntries: Object.entries(script.customFields ?? {}).map(([k, v]) => [k, String(v)]),
  };
}

function toWirePatch(dirty: Partial<FormState>): Partial<ScriptPatchInput> {
  const out: Partial<ScriptPatchInput> = {};
  if ("label" in dirty) out.label = dirty.label || null;
  if ("rollNumber" in dirty) out.rollNumber = dirty.rollNumber || null;
  if ("studentName" in dirty) out.studentName = dirty.studentName || null;
  if ("gender" in dirty) out.gender = dirty.gender || null;
  if ("subject" in dirty) out.subject = dirty.subject || null;
  if ("section" in dirty) out.section = dirty.section || null;
  if ("groundTruthMarksTotal" in dirty) out.groundTruthMarksTotal = dirty.groundTruthMarksTotal ?? null;
  if ("tags" in dirty) out.tags = dirty.tags;
  if ("notes" in dirty) out.notes = dirty.notes || null;
  if ("customFieldsEntries" in dirty) {
    out.customFields = Object.fromEntries((dirty.customFieldsEntries ?? []).filter(([k]) => k.trim()));
  }
  return out;
}

export function ReviewScriptForm({
  script,
  onAutosaveChange,
}: {
  script: ReviewScriptDetail;
  onAutosaveChange?: (controller: { status: SaveStatus; flush: () => Promise<void> }) => void;
}) {
  const patch = usePatchScript(script.id);
  const suggestions = useFieldSuggestions();
  const [state, setState] = useState(() => toFormState(script));
  const baselineRef = useRef(state);

  useEffect(() => {
    const next = toFormState(script);
    setState(next);
    baselineRef.current = next;
    // `script.id` only: a background refetch mid-edit must not clobber
    // what the user is typing, but switching scripts must reset the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [script.id]);

  const autosave = useAutosave<Partial<ScriptPatchInput>>(wire => patch.mutateAsync(wire));

  useEffect(() => {
    onAutosaveChange?.({ status: autosave.status, flush: autosave.flush });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autosave.status, autosave.flush]);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setState(prev => {
      const next = { ...prev, [key]: value };
      const dirty = pickDirty(next, baselineRef.current);
      if (Object.keys(dirty).length > 0) {
        autosave.schedule(toWirePatch(dirty));
      }
      return next;
    });
  }

  return (
    <div className="space-y-4">
      <Field>
        <FieldLabel>Label</FieldLabel>
        <SuggestCombobox
          ariaLabel="Label"
          value={state.label}
          onValueChange={v => update("label", v)}
          suggestions={suggestions.data?.labels ?? []}
        />
      </Field>
      <Field>
        <FieldLabel>Roll number</FieldLabel>
        <Input value={state.rollNumber} onChange={e => update("rollNumber", e.target.value)} />
      </Field>
      <Field>
        <FieldLabel>Student name</FieldLabel>
        <Input value={state.studentName} onChange={e => update("studentName", e.target.value)} />
      </Field>
      <Field>
        <FieldLabel>Gender</FieldLabel>
        <ToggleGroup
          variant="outline"
          className="w-full"
          value={state.gender ? [state.gender] : []}
          onValueChange={v => update("gender", v[0] ?? "")}
        >
          <ToggleGroupItem value="male">Male</ToggleGroupItem>
          <ToggleGroupItem value="female">Female</ToggleGroupItem>
        </ToggleGroup>
      </Field>
      <Field>
        <FieldLabel>Subject</FieldLabel>
        <SuggestCombobox
          ariaLabel="Subject"
          value={state.subject}
          onValueChange={v => update("subject", v)}
          suggestions={suggestions.data?.subjects ?? []}
        />
      </Field>
      <Field>
        <FieldLabel>Section</FieldLabel>
        <SuggestCombobox
          ariaLabel="Section"
          value={state.section}
          onValueChange={v => update("section", v)}
          suggestions={suggestions.data?.sections ?? []}
        />
      </Field>
      <Field>
        <FieldLabel>Ground-truth total marks</FieldLabel>
        <NumberField
          min={0}
          value={state.groundTruthMarksTotal}
          onValueChange={v => update("groundTruthMarksTotal", v)}
        >
          <NumberFieldGroup>
            <NumberFieldDecrement />
            <NumberFieldInput />
            <NumberFieldIncrement />
          </NumberFieldGroup>
        </NumberField>
      </Field>
      <Field>
        <FieldLabel>Tags</FieldLabel>
        <TagInput value={state.tags} onValueChange={v => update("tags", v)}>
          <TagInputGroup>
            <TagInputField placeholder="Add tag" />
          </TagInputGroup>
        </TagInput>
      </Field>
      <Field>
        <FieldLabel>Script notes</FieldLabel>
        <Textarea value={state.notes} onChange={e => update("notes", e.target.value)} rows={3} />
      </Field>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <Label>Custom fields</Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => update("customFieldsEntries", [...state.customFieldsEntries, ["", ""]])}
          >
            <LuPlus />
            Add field
          </Button>
        </div>
        {state.customFieldsEntries.map(([key, value], i) => (
          <div key={i} className="mb-2 flex gap-2">
            <InputGroup className="flex-1">
              <InputGroupInput
                placeholder="key"
                value={key}
                onChange={e =>
                  update(
                    "customFieldsEntries",
                    state.customFieldsEntries.map((row, j) => (j === i ? [e.target.value, row[1]] : row)),
                  )
                }
              />
            </InputGroup>
            <InputGroup className="flex-1">
              <InputGroupInput
                placeholder="value"
                value={value}
                onChange={e =>
                  update(
                    "customFieldsEntries",
                    state.customFieldsEntries.map((row, j) => (j === i ? [row[0], e.target.value] : row)),
                  )
                }
              />
            </InputGroup>
            <Button
              type="button"
              variant="ghost"
              size="icon-md"
              onClick={() =>
                update(
                  "customFieldsEntries",
                  state.customFieldsEntries.filter((_, j) => j !== i),
                )
              }
            >
              <LuX />
            </Button>
          </div>
        ))}
      </div>

      <SaveStatusLabel status={autosave.status} />
    </div>
  );
}
